/**
 * OpenUI Tool Specs
 *
 * Declarative tool surface the model may wire via Query() / Mutation().
 * Executed at runtime by the client toolProvider (no LLM round-trip).
 *
 * Dependencies:
 * - @openuidev/lang-core ToolSpec shape
 */

import type { ToolSpec } from '@openuidev/lang-core'

export const LOSI_OPENUI_TOOL_SPECS: ToolSpec[] = [
  {
    name: 'list_space_tasks',
    description:
      'List tasks in a Space. Omit space_id to use the currently selected Space.',
    inputSchema: {
      type: 'object',
      properties: {
        space_id: { type: 'string', description: 'Space id (optional if a Space is selected)' },
        status: {
          type: 'string',
          description: 'Optional status filter (e.g. todo, in_progress, done)'
        },
        limit: { type: 'number', description: 'Max rows (default 25)' }
      },
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        rows: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string' },
              title: { type: 'string' },
              status: { type: 'string' },
              due: { type: 'string' },
              summary: { type: 'string' }
            }
          }
        },
        count: { type: 'number' },
        open_count: { type: 'number' },
        done_count: { type: 'number' }
      }
    },
    annotations: { readOnlyHint: true }
  },
  {
    name: 'create_space_task',
    description: 'Create a task in a Space. Omit space_id to use the selected Space.',
    inputSchema: {
      type: 'object',
      properties: {
        space_id: { type: 'string' },
        title: { type: 'string' },
        description: { type: 'string' },
        status: { type: 'string' },
        due_date: { type: 'string', description: 'ISO date or YYYY-MM-DD' }
      },
      required: ['title'],
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        success: { type: 'boolean' },
        id: { type: 'string' },
        title: { type: 'string' }
      }
    },
    annotations: { destructiveHint: false }
  },
  {
    name: 'update_space_task',
    description: 'Update a Space task status or title.',
    inputSchema: {
      type: 'object',
      properties: {
        space_id: { type: 'string' },
        task_id: { type: 'string' },
        title: { type: 'string' },
        status: { type: 'string' }
      },
      required: ['task_id'],
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        success: { type: 'boolean' },
        id: { type: 'string' }
      }
    }
  },
  {
    name: 'list_space_notes',
    description: 'List notes in a Space. Omit space_id to use the selected Space.',
    inputSchema: {
      type: 'object',
      properties: {
        space_id: { type: 'string' },
        limit: { type: 'number' }
      },
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        rows: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string' },
              title: { type: 'string' },
              updated_at: { type: 'string' }
            }
          }
        },
        count: { type: 'number' }
      }
    },
    annotations: { readOnlyHint: true }
  },
  {
    name: 'list_spaces',
    description:
      'List Spaces in the current workspace. Omit workspace_id to use the selected workspace.',
    inputSchema: {
      type: 'object',
      properties: {
        workspace_id: { type: 'string' },
        q: { type: 'string', description: 'Optional search query' },
        limit: { type: 'number' }
      },
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        rows: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string' },
              name: { type: 'string' },
              description: { type: 'string' }
            }
          }
        },
        count: { type: 'number' }
      }
    },
    annotations: { readOnlyHint: true }
  },
  {
    name: 'list_crm_contacts',
    description:
      'List Nexus CRM contacts for the workspace. Omit workspace_id to use the selected workspace.',
    inputSchema: {
      type: 'object',
      properties: {
        workspace_id: { type: 'string' },
        q: { type: 'string', description: 'Optional name/email filter' },
        limit: { type: 'number' }
      },
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        rows: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string' },
              name: { type: 'string' },
              email: { type: 'string' },
              company: { type: 'string' },
              status: { type: 'string' }
            }
          }
        },
        count: { type: 'number' }
      }
    },
    annotations: { readOnlyHint: true }
  },
  {
    name: 'get_task_metrics',
    description:
      'Return open/done/due-today KPI counts for a Space (uses list_space_tasks under the hood).',
    inputSchema: {
      type: 'object',
      properties: {
        space_id: { type: 'string' }
      },
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        open: { type: 'number' },
        done: { type: 'number' },
        due_today: { type: 'number' },
        total: { type: 'number' }
      }
    },
    annotations: { readOnlyHint: true }
  }
]

export const LOSI_OPENUI_TOOL_NAMES = LOSI_OPENUI_TOOL_SPECS.map(tool => tool.name)
